import React, { useEffect, useRef, useState, useCallback, useImperativeHandle, forwardRef } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { TransformControls } from 'three/examples/jsm/controls/TransformControls';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { CCDIKSolver } from 'three/examples/jsm/animation/CCDIKSolver';

const ThreeCanvas = forwardRef(function ThreeCanvas({
  modelUrl,
  texturedModelUrl = null, // URL to load textures from (for pose mode)
  backgroundColor = '#1a1a2e',
  isPlaying = true,
  animationSpeed = 1,
  scale = 1,
  rotation = 0,
  orbitTheta = 0,
  orbitPhi = 75,
  distance = 3,
  fov = 50,
  targetY = 0.85,
  showGrid = false,
  onAnimationsLoaded,
  onBonesLoaded,
  selectedBone,
  onBoneSelect,
  onBoneTransformChange,
  enablePosing = false,
  // Keyframe animation props
  keyframes = [],
  currentFrame = 0,
  totalFrames = 240,
  fps = 24,
  useKeyframeAnimation = false,
  useBuiltInAnimation = false, // Use the GLTF's built-in animation (Meshy walk)
  onFrameUpdate,
  onBoneDataUpdate, // Callback for real-time bone transform updates (for 2D sprite rendering)
  // Character transform props (for world position, rotation, scale)
  characterPosition = { x: 0, y: 0, z: 0 },
  characterRotation = { x: 0, y: 0, z: 0 },
  characterScale = { x: 1, y: 1, z: 1 },
  characterVisible = true,
  characterOpacity = 1,
  // Foot IK / locking props for root motion
  enableFootLocking = false,
  footContactData = null, // { left: [{frame, isPlanted}...], right: [...] }
  // Shot settings (camera & light from timeline keyframes)
  cameraSettings = null, // { position: {x,y,z}, rotation: {x,y,z} }
  lightSettings = null, // { position: {x,y,z}, intensity: number }
  // Camera control callbacks (for mouse interaction)
  onCameraChange = null, // Called when camera is changed via mouse
  className = ''
}, ref) {
  const containerRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);
  const transformControlsRef = useRef(null);
  const modelRef = useRef(null);
  const skeletonRef = useRef(null);
  const bonesRef = useRef({});
  const boneHelpersRef = useRef([]);
  const mixerRef = useRef(null);
  const clockRef = useRef(new THREE.Clock());
  const animationIdRef = useRef(null);
  const actionsRef = useRef([]);
  const raycasterRef = useRef(new THREE.Raycaster());
  const mouseRef = useRef(new THREE.Vector2());
  const cachedTextureRef = useRef(null); // Cache textures from textured model
  const mainLightRef = useRef(null); // Main directional light for keyframing
  
  // Mouse camera control refs
  const isDraggingRef = useRef(false);
  const dragModeRef = useRef(null); // 'pan' | 'orbit' | 'roll'
  const lastMousePosRef = useRef({ x: 0, y: 0 });
  const cameraSettingsRef = useRef(cameraSettings);
  const onCameraChangeRef = useRef(onCameraChange);
  
  // Keep cameraSettings ref updated
  useEffect(() => {
    cameraSettingsRef.current = cameraSettings;
  }, [cameraSettings]);
  
  // Keep onCameraChange ref updated (to avoid recreation of Three.js scene)
  useEffect(() => {
    onCameraChangeRef.current = onCameraChange;
  }, [onCameraChange]);
  
  // Keyframe animation refs
  const keyframeTimeRef = useRef(0);
  const lastKeyframesRef = useRef([]);
  const isPlayingRef = useRef(false); // Track playing state for animation loop
  const useBuiltInAnimationRef = useRef(false); // Track built-in animation state
  const useKeyframeAnimationRef = useRef(false); // Track keyframe animation state
  
  // Foot IK refs for locking planted feet
  const leftFootBoneRef = useRef(null);
  const rightFootBoneRef = useRef(null);
  const leftFootPlantPosRef = useRef(null); // World position where left foot should stay
  const rightFootPlantPosRef = useRef(null); // World position where right foot should stay
  const enableFootLockingRef = useRef(false);
  
  // Bone data update callback ref (for 2D sprite rendering)
  const onBoneDataUpdateRef = useRef(onBoneDataUpdate);
  const lastBoneUpdateTimeRef = useRef(0);
  
  // Keep onBoneDataUpdate ref updated
  useEffect(() => {
    onBoneDataUpdateRef.current = onBoneDataUpdate;
  }, [onBoneDataUpdate]);
  const footContactDataRef = useRef(null);
  
  // CCDIKSolver refs
  const ikSolverRef = useRef(null);
  const leftFootTargetRef = useRef(null);  // Object3D for left foot IK target
  const rightFootTargetRef = useRef(null); // Object3D for right foot IK target
  const skinnedMeshRef = useRef(null);     // Reference to the skinned mesh for IK
  const legBoneIndicesRef = useRef(null);  // Cached leg bone indices
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hoveredBone, setHoveredBone] = useState(null);
  
  // ====== FOOT IK SETUP FUNCTION ======
  const setupFootIK = useCallback((skeleton, skinnedMesh, scene) => {
    if (!skeleton || !skinnedMesh) {
      console.log('Cannot setup Foot IK: missing skeleton or skinned mesh');
      return;
    }
    
    const bones = skeleton.bones;
    
    // Find bone indices by name patterns
    const findBoneIndex = (patterns) => {
      for (let i = 0; i < bones.length; i++) {
        const name = bones[i].name.toLowerCase();
        for (const pattern of patterns) {
          if (name.includes(pattern.toLowerCase())) {
            return i;
          }
        }
      }
      return -1;
    };
    
    // Find left leg bones
    const leftHipIdx = findBoneIndex(['lefthip', 'left_hip', 'l_hip', 'hip_l', 'leftupleg', 'left_upleg', 'l_upleg', 'thigh_l', 'leftthigh']);
    const leftKneeIdx = findBoneIndex(['leftknee', 'left_knee', 'l_knee', 'knee_l', 'leftleg', 'left_leg', 'l_leg', 'shin_l', 'calf_l']);
    const leftFootIdx = findBoneIndex(['leftfoot', 'left_foot', 'l_foot', 'foot_l', 'foot.l']);
    
    // Find right leg bones  
    const rightHipIdx = findBoneIndex(['righthip', 'right_hip', 'r_hip', 'hip_r', 'rightupleg', 'right_upleg', 'r_upleg', 'thigh_r', 'rightthigh']);
    const rightKneeIdx = findBoneIndex(['rightknee', 'right_knee', 'r_knee', 'knee_r', 'rightleg', 'right_leg', 'r_leg', 'shin_r', 'calf_r']);
    const rightFootIdx = findBoneIndex(['rightfoot', 'right_foot', 'r_foot', 'foot_r', 'foot.r']);
    
    console.log('Foot IK bone indices:', {
      leftLeg: { hip: leftHipIdx, knee: leftKneeIdx, foot: leftFootIdx },
      rightLeg: { hip: rightHipIdx, knee: rightKneeIdx, foot: rightFootIdx }
    });
    
    // Validate we found all required bones
    if (leftHipIdx < 0 || leftKneeIdx < 0 || leftFootIdx < 0 ||
        rightHipIdx < 0 || rightKneeIdx < 0 || rightFootIdx < 0) {
      console.warn('Could not find all leg bones for IK setup');
      return;
    }
    
    // Store bone indices
    legBoneIndicesRef.current = {
      left: { hip: leftHipIdx, knee: leftKneeIdx, foot: leftFootIdx },
      right: { hip: rightHipIdx, knee: rightKneeIdx, foot: rightFootIdx }
    };
    
    // Store foot bone references
    leftFootBoneRef.current = bones[leftFootIdx];
    rightFootBoneRef.current = bones[rightFootIdx];
    
    // Create IK target objects (invisible spheres that define where feet should be)
    const leftTarget = new THREE.Object3D();
    leftTarget.name = 'leftFootIKTarget';
    scene.add(leftTarget);
    leftFootTargetRef.current = leftTarget;
    
    const rightTarget = new THREE.Object3D();
    rightTarget.name = 'rightFootIKTarget';
    scene.add(rightTarget);
    rightFootTargetRef.current = rightTarget;
    
    // Note: We'll create the IK solver dynamically when foot locking is enabled
    // because we need to properly set iteration counts and constraints based on the model
    
    console.log('Foot IK setup complete - targets created');
    
    // Note: We DON'T create the CCDIKSolver here because it requires the mesh to be 
    // fully initialized with proper world matrices. The solver will be created lazily
    // when foot locking is first enabled.
  }, []);
  
  // ====== CREATE IK SOLVER (lazy initialization) ======
  const createIKSolverIfNeeded = useCallback(() => {
    // Already created
    if (ikSolverRef.current) return true;
    
    // Check prerequisites
    if (!skinnedMeshRef.current || !legBoneIndicesRef.current) {
      console.log('Cannot create IK solver: missing mesh or bone indices');
      return false;
    }
    
    if (!leftFootTargetRef.current || !rightFootTargetRef.current) {
      console.log('Cannot create IK solver: missing foot targets');
      return false;
    }
    
    const indices = legBoneIndicesRef.current;
    
    try {
      const ikConfig = [
        // Left leg IK
        {
          target: leftFootTargetRef.current,
          effector: indices.left.foot,
          links: [
            { index: indices.left.knee, limitation: new THREE.Vector3(1, 0, 0) },
            { index: indices.left.hip }
          ],
          iteration: 10,
          minAngle: 0.0,
          maxAngle: Math.PI * 0.5
        },
        // Right leg IK  
        {
          target: rightFootTargetRef.current,
          effector: indices.right.foot,
          links: [
            { index: indices.right.knee, limitation: new THREE.Vector3(1, 0, 0) },
            { index: indices.right.hip }
          ],
          iteration: 10,
          minAngle: 0.0,
          maxAngle: Math.PI * 0.5
        }
      ];
      
      ikSolverRef.current = new CCDIKSolver(skinnedMeshRef.current, ikConfig);
      console.log('CCDIKSolver created successfully (lazy init)');
      return true;
      
    } catch (err) {
      console.error('Failed to create CCDIKSolver:', err);
      return false;
    }
  }, []);
  
  // ====== UPDATE FOOT IK FUNCTION ======
  // Called during playback to lock planted feet
  const updateFootIK = useCallback((currentFrame, totalFrames) => {
    if (!enableFootLockingRef.current || !footContactDataRef.current) {
      return;
    }
    
    // Don't try to update IK if we don't have the necessary bones
    if (!leftFootBoneRef.current || !rightFootBoneRef.current) {
      return;
    }
    
    const leftContacts = footContactDataRef.current.left;
    const rightContacts = footContactDataRef.current.right;
    
    if (!leftContacts || !rightContacts || leftContacts.length === 0 || rightContacts.length === 0) {
      return;
    }
    
    // Get contact state for current frame
    const frameIndex = Math.min(
      Math.max(0, Math.floor(currentFrame)), 
      leftContacts.length - 1, 
      rightContacts.length - 1
    );
    
    const leftContact = leftContacts[frameIndex];
    const rightContact = rightContacts[frameIndex];
    
    // Get current foot world positions
    const leftFootBone = leftFootBoneRef.current;
    const rightFootBone = rightFootBoneRef.current;
    
    const leftFootWorld = new THREE.Vector3();
    const rightFootWorld = new THREE.Vector3();
    
    try {
      leftFootBone.getWorldPosition(leftFootWorld);
      rightFootBone.getWorldPosition(rightFootWorld);
    } catch (err) {
      // Bones might not be ready yet
      return;
    }
    
    // Handle left foot planting/lifting
    if (leftContact?.isPlanted) {
      if (!leftFootPlantPosRef.current) {
        // Foot just planted - record position
        leftFootPlantPosRef.current = leftFootWorld.clone();
      }
      // Update IK target to planted position
      if (leftFootTargetRef.current && leftFootPlantPosRef.current) {
        leftFootTargetRef.current.position.copy(leftFootPlantPosRef.current);
      }
    } else {
      // Foot lifted - clear plant position
      leftFootPlantPosRef.current = null;
    }
    
    // Handle right foot planting/lifting
    if (rightContact?.isPlanted) {
      if (!rightFootPlantPosRef.current) {
        // Foot just planted - record position
        rightFootPlantPosRef.current = rightFootWorld.clone();
      }
      // Update IK target to planted position
      if (rightFootTargetRef.current && rightFootPlantPosRef.current) {
        rightFootTargetRef.current.position.copy(rightFootPlantPosRef.current);
      }
    } else {
      // Foot lifted - clear plant position
      rightFootPlantPosRef.current = null;
    }
    
    // Only run IK solver if at least one foot is planted
    const shouldRunIK = leftFootPlantPosRef.current || rightFootPlantPosRef.current;
    
    if (shouldRunIK) {
      // Try to create IK solver if not already created
      if (!ikSolverRef.current) {
        createIKSolverIfNeeded();
      }
      
      // Update IK solver if it exists
      if (ikSolverRef.current) {
        try {
          ikSolverRef.current.update();
        } catch (err) {
          // IK solver error - disable it
          console.error('IK solver error:', err);
          ikSolverRef.current = null;
        }
      }
    }
  }, [createIKSolverIfNeeded]);

  // Quaternion SLERP interpolation helper
  const slerpQuaternion = useCallback((q1, q2, t) => {
    const result = new THREE.Quaternion(q1[0], q1[1], q1[2], q1[3]);
    const target = new THREE.Quaternion(q2[0], q2[1], q2[2], q2[3]);
    result.slerp(target, t);
    return result;
  }, []);
  
  // Get interpolated bone transforms at a given frame (with loop support)
  const getInterpolatedPose = useCallback((frame, kfs, totalFrames = 48) => {
    if (!kfs || kfs.length === 0) return {};
    
    // Normalize frame to the animation duration for looping
    const normalizedFrame = ((frame % totalFrames) + totalFrames) % totalFrames;
    
    // Sort keyframes by frame number
    const sortedKfs = [...kfs].sort((a, b) => a.frame - b.frame);
    
    // Find surrounding keyframes
    let prevKf = null;
    let nextKf = null;
    
    for (const kf of sortedKfs) {
      if (kf.frame <= normalizedFrame) {
        prevKf = kf;
      }
      if (kf.frame >= normalizedFrame && !nextKf) {
        nextKf = kf;
      }
    }
    
    // If no previous keyframe, use last (for looping)
    if (!prevKf) prevKf = sortedKfs[sortedKfs.length - 1];
    // If no next keyframe, use first (for looping)
    if (!nextKf) nextKf = sortedKfs[0];
    
    // Handle looping interpolation (when prev > next in terms of frame)
    let t;
    if (prevKf.frame === nextKf.frame) {
      // Same keyframe, no interpolation needed
      return prevKf.bones || {};
    } else if (prevKf.frame > nextKf.frame) {
      // Looping case: interpolate from last keyframe to first
      // Calculate how far we are from prev to end, plus from start to next
      const framesToEnd = totalFrames - prevKf.frame;
      const framesToNext = nextKf.frame;
      const totalGap = framesToEnd + framesToNext;
      
      if (normalizedFrame >= prevKf.frame) {
        // We're between prevKf and the end of the cycle
        t = (normalizedFrame - prevKf.frame) / totalGap;
      } else {
        // We're between start of cycle and nextKf
        t = (framesToEnd + normalizedFrame) / totalGap;
      }
    } else {
      // Normal case: prevKf < nextKf
      t = (normalizedFrame - prevKf.frame) / (nextKf.frame - prevKf.frame);
    }
    
    // Clamp t to [0, 1]
    t = Math.max(0, Math.min(1, t));
    
    // Interpolate all bones
    const interpolatedBones = {};
    const allBoneNames = new Set([
      ...Object.keys(prevKf.bones || {}),
      ...Object.keys(nextKf.bones || {})
    ]);
    
    allBoneNames.forEach(boneName => {
      const prevTransform = prevKf.bones?.[boneName];
      const nextTransform = nextKf.bones?.[boneName];
      
      if (prevTransform && nextTransform) {
        // Interpolate rotation using SLERP
        const prevRot = prevTransform.rotation || [0, 0, 0, 1];
        const nextRot = nextTransform.rotation || [0, 0, 0, 1];
        const interpQuat = slerpQuaternion(prevRot, nextRot, t);
        
        interpolatedBones[boneName] = {
          rotation: [interpQuat.x, interpQuat.y, interpQuat.z, interpQuat.w]
        };
      } else if (prevTransform) {
        interpolatedBones[boneName] = prevTransform;
      } else if (nextTransform) {
        interpolatedBones[boneName] = nextTransform;
      }
    });
    
    return interpolatedBones;
  }, [slerpQuaternion]);
  
  // Apply bone transforms to the skeleton
  // CRITICAL: We must modify bones from skeletonRef.current.bones, NOT from bonesRef
  // because the skeleton's bones array is what the skinned mesh uses for rendering
  const applyBoneTransforms = useCallback((transforms) => {
    if (!transforms || Object.keys(transforms).length === 0) {
      return;
    }
    
    // IMPORTANT: Use skeleton's bones array for animation, not bonesRef
    // skeletonRef.current.bones is what the SkinnedMesh actually uses
    if (!skeletonRef.current || !skeletonRef.current.bones) {
      console.log('applyBoneTransforms: No skeleton bones available');
      return;
    }
    
    const skeletonBones = skeletonRef.current.bones;
    let appliedCount = 0;
    
    Object.entries(transforms).forEach(([boneName, transform]) => {
      if (!transform.rotation) return;
      
      // Find bone in skeleton's bones array by name
      const bone = skeletonBones.find(b => b.name === boneName);
      if (bone) {
        bone.quaternion.set(
          transform.rotation[0],
          transform.rotation[1],
          transform.rotation[2],
          transform.rotation[3]
        );
        appliedCount++;
      }
    });
    
    if (appliedCount > 0) {
      // Update the skeleton - this recalculates bone matrices
      skeletonRef.current.update();
      
      // Force the skinned mesh to use the updated skeleton
      if (modelRef.current) {
        modelRef.current.traverse((child) => {
          if (child.isSkinnedMesh) {
            child.skeleton.update();
          }
        });
        modelRef.current.updateMatrixWorld(true);
      }
    }
  }, []);
  
  // Expose methods via ref for parent component
  useImperativeHandle(ref, () => ({
    // Get all current bone transforms (for keyframing)
    getAllBoneTransforms: () => {
      const transforms = {};
      Object.entries(bonesRef.current).forEach(([name, bone]) => {
        transforms[name] = {
          rotation: [bone.quaternion.x, bone.quaternion.y, bone.quaternion.z, bone.quaternion.w],
          position: [bone.position.x, bone.position.y, bone.position.z]
        };
      });
      return transforms;
    },
    
    // Set bone transform (for playback)
    setBoneTransform: (boneName, transform) => {
      const bone = bonesRef.current[boneName];
      if (!bone) return;
      
      if (transform.rotation) {
        bone.quaternion.set(
          transform.rotation[0],
          transform.rotation[1],
          transform.rotation[2],
          transform.rotation[3]
        );
      }
      if (transform.position) {
        bone.position.set(
          transform.position[0],
          transform.position[1],
          transform.position[2]
        );
      }
    },
    
    // Set all bone transforms at once (for keyframe playback)
    setAllBoneTransforms: (transforms) => {
      Object.entries(transforms).forEach(([name, transform]) => {
        const bone = bonesRef.current[name];
        if (!bone) return;
        
        if (transform.rotation) {
          bone.quaternion.set(
            transform.rotation[0],
            transform.rotation[1],
            transform.rotation[2],
            transform.rotation[3]
          );
        }
      });
    },
    
    // Get specific bone transform
    getBoneTransform: (boneName) => {
      const bone = bonesRef.current[boneName];
      if (!bone) return null;
      
      return {
        rotation: [bone.quaternion.x, bone.quaternion.y, bone.quaternion.z, bone.quaternion.w],
        position: [bone.position.x, bone.position.y, bone.position.z]
      };
    },
    
    // Stop all animations (for manual posing)
    stopAnimations: () => {
      actionsRef.current.forEach(action => {
        action.stop();
      });
      if (mixerRef.current) {
        mixerRef.current.stopAllAction();
      }
    },
    
    // Reset bone to bind pose
    resetBone: (boneName) => {
      const bone = bonesRef.current[boneName];
      if (!bone) return;
      
      bone.quaternion.identity();
      bone.position.set(0, 0, 0);
    },
    
    // Extract keyframes from GLTF animation (for editable walk cycle)
    // Also extracts root motion data for natural character movement
    // animationType: 'walk', 'run', 'jump', 'punch', 'kick', 'block'
    extractAnimationKeyframes: (fps = 24, totalFrames = 48, animationType = 'walk') => {
      if (!mixerRef.current || actionsRef.current.length === 0) {
        console.warn('No animations loaded to extract');
        return null;
      }
      
      const action = actionsRef.current[0];
      const clip = action.getClip();
      if (!clip) return null;
      
      const duration = clip.duration;
      const keyframes = [];
      
      // Root motion tracking - find hip/root bone
      const rootBoneNames = ['Hips', 'mixamorig:Hips', 'Root', 'root', 'Armature'];
      let rootBone = null;
      if (skeletonRef.current && skeletonRef.current.bones) {
        rootBone = skeletonRef.current.bones.find(b => 
          rootBoneNames.some(name => b.name.includes(name))
        );
      }
      
      // Animation-specific settings including Y offset keyframes
      const animationSettings = {
        walk: { 
          speedMultiplier: 1.0, 
          distancePerCycle: 1.3, 
          name: 'Walk Cycle',
          // Y offset keyframes to keep feet grounded during walk cycle
          // These values were manually analyzed from the walk animation
          yOffsetKeyframes: [
            { frame: 0, offset: 0 },
            { frame: 6, offset: 0 },
            { frame: 12, offset: -0.05 },
            { frame: 18, offset: -0.05 },
            { frame: 24, offset: 0 },
            { frame: 30, offset: 0 },
            { frame: 36, offset: -0.05 },
            { frame: 42, offset: -0.05 },
            { frame: 48, offset: 0 }
          ]
        },
        run: { speedMultiplier: 1.5, distancePerCycle: 2.5, name: 'Run Cycle', yOffsetKeyframes: [] },
        jump: { speedMultiplier: 1.0, distancePerCycle: 0, name: 'Jump', isJump: true, yOffsetKeyframes: [] },
        punch: { speedMultiplier: 2.0, distancePerCycle: 0.3, name: 'Punch', isCombat: true, yOffsetKeyframes: [] },
        kick: { speedMultiplier: 1.5, distancePerCycle: 0.5, name: 'Kick', isCombat: true, yOffsetKeyframes: [] },
        jab: { speedMultiplier: 2.0, distancePerCycle: 0, name: 'Jab', isCombat: true, yOffsetKeyframes: [] },
        block: { speedMultiplier: 1.0, distancePerCycle: 0, name: 'Block', isCombat: true, yOffsetKeyframes: [] },
        combo: { speedMultiplier: 1.5, distancePerCycle: 0.2, name: 'Combo', isCombat: true, yOffsetKeyframes: [] },
        idle: { speedMultiplier: 1.0, distancePerCycle: 0, name: 'Idle', yOffsetKeyframes: [] },
        wave: { speedMultiplier: 1.0, distancePerCycle: 0, name: 'Wave', yOffsetKeyframes: [] },
        dance: { speedMultiplier: 1.0, distancePerCycle: 0, name: 'Dance', yOffsetKeyframes: [] }
      };
      
      const settings = animationSettings[animationType] || animationSettings.walk;
      
      // Track root bone position for root motion extraction
      let rootMotionData = {
        enabled: !['jump', 'block', 'jab', 'idle', 'wave', 'dance'].includes(animationType),
        distancePerCycle: settings.distancePerCycle,
        direction: { x: 0, z: 1 }, // Default forward direction
        framePositions: [],
        yOffsetKeyframes: settings.yOffsetKeyframes || []  // Y offset keyframes for foot grounding
      };
      
      let firstRootPos = null;
      let lastRootPos = null;
      
      // Ensure the action is playing so we can sample it
      const wasPlaying = action.isRunning();
      const wasPaused = action.paused;
      action.paused = false;
      action.play();
      
      // FIRST PASS: Sample frame-by-frame positions for root motion (XZ only)
      for (let frame = 0; frame <= totalFrames; frame++) {
        const time = (frame / totalFrames) * duration;
        mixerRef.current.setTime(time);
        mixerRef.current.update(0);
        
        // Get hip position for XZ root motion
        if (rootBone) {
          const worldPos = new THREE.Vector3();
          rootBone.getWorldPosition(worldPos);
          
          if (frame === 0) {
            firstRootPos = worldPos.clone();
          }
          
          rootMotionData.framePositions.push({
            frame,
            x: worldPos.x - (firstRootPos?.x || 0),
            y: worldPos.y - (firstRootPos?.y || 0),
            z: worldPos.z - (firstRootPos?.z || 0)
          });
          
          lastRootPos = worldPos.clone();
        }
      }
      
      console.log(`[RootMotion] Extracted ${rootMotionData.framePositions.length} positions, ${rootMotionData.yOffsetKeyframes.length} Y offset keyframes`);
      
      // SECOND PASS: Sample keyframes at intervals (for bone animation)
      const sampleInterval = Math.max(1, Math.floor(totalFrames / 8));
      
      for (let frame = 0; frame < totalFrames; frame += sampleInterval) {
        const time = (frame / totalFrames) * duration;
        
        // Set mixer to this time AND update to apply the animation!
        // CRITICAL: setTime() only sets the time, update(0) actually applies the pose
        mixerRef.current.setTime(time);
        mixerRef.current.update(0); // Force apply the animation at current time
        
        // Collect bone transforms at this frame from the skeleton's bones
        const bones = {};
        if (skeletonRef.current && skeletonRef.current.bones) {
          skeletonRef.current.bones.forEach((bone) => {
            bones[bone.name] = {
              rotation: [
                bone.quaternion.x,
                bone.quaternion.y,
                bone.quaternion.z,
                bone.quaternion.w
              ]
            };
          });
        }
        
        keyframes.push({
          id: `extracted-${frame}`,
          frame: frame,
          bones: bones
        });
      }
      
      // Calculate root motion distance from animation data
      if (firstRootPos && lastRootPos) {
        const delta = new THREE.Vector3().subVectors(lastRootPos, firstRootPos);
        const extractedDistance = delta.length();
        
        // Use extracted distance if significant, otherwise use preset
        if (extractedDistance >= 0.3) {
          rootMotionData.distancePerCycle = extractedDistance;
          delta.normalize();
          rootMotionData.direction = { x: delta.x, z: delta.z };
        }
      }
      
      console.log(`Root motion for ${animationType}: distance=${rootMotionData.distancePerCycle}, enabled=${rootMotionData.enabled}`);
      
      // Add final keyframe at exactly totalFrames for perfect looping
      // This keyframe has the same pose as frame 0 (animation loops back)
      // Without this, interpolation would jump from the last sampled frame to frame 0
      if (keyframes.length > 0) {
        const firstKf = keyframes[0];
        keyframes.push({
          id: `extracted-${totalFrames}`,
          frame: totalFrames,  // Frame 48 for a 48-frame animation
          bones: { ...firstKf.bones }
        });
      }
      
      // Reset mixer time and restore action state
      mixerRef.current.setTime(0);
      mixerRef.current.update(0);
      action.paused = wasPaused;
      if (!wasPlaying) {
        action.stop();
      }
      
      console.log(`Extracted ${keyframes.length} keyframes for ${animationType}, ${rootMotionData.framePositions.length} root positions`);
      return {
        name: `${settings.name} (Extracted)`,
        description: `${settings.name} animation - fully editable`,
        fps: fps,
        total_frames: totalFrames,
        keyframes: keyframes,
        rootMotion: rootMotionData,
        animationType: animationType
      };
    },
    
    // Extract keyframes from a specific animation GLB URL with TRUE ROOT MOTION
    // This extracts the hip bone's world movement and stores it separately,
    // so the character can be moved by applying this track to the container
    extractAnimationFromUrl: async (animationUrl, fps = 24, totalFrames = 48, animationType = 'walk') => {
      return new Promise((resolve, reject) => {
        const loader = new GLTFLoader();
        
        console.log(`[TrueRootMotion] Loading animation GLB from: ${animationUrl}`);
        
        loader.load(
          animationUrl,
          (gltf) => {
            try {
              // Check if the loaded model has animations
              if (!gltf.animations || gltf.animations.length === 0) {
                console.warn('[TrueRootMotion] Loaded GLB has no animations');
                resolve(null);
                return;
              }
              
              const clip = gltf.animations[0];
              const duration = clip.duration;
              
              // Create a temporary mixer for this model
              const tempMixer = new THREE.AnimationMixer(gltf.scene);
              const action = tempMixer.clipAction(clip);
              action.play();
              
              // Find the skeleton in the loaded model
              let tempSkeleton = null;
              gltf.scene.traverse((node) => {
                if (node.isSkinnedMesh && node.skeleton) {
                  tempSkeleton = node.skeleton;
                }
              });
              
              if (!tempSkeleton) {
                console.warn('[TrueRootMotion] No skeleton found in loaded animation GLB');
                resolve(null);
                return;
              }
              
              // Find root/hip bone - this is the bone whose movement we extract
              const rootBoneNames = ['Hips', 'mixamorig:Hips', 'Root', 'root', 'Armature', 'pelvis', 'Pelvis'];
              const hipBone = tempSkeleton.bones.find(b => 
                rootBoneNames.some(name => b.name.toLowerCase().includes(name.toLowerCase()))
              );
              
              // Find foot bones for ground contact tracking
              const leftFootNames = ['LeftFoot', 'mixamorig:LeftFoot', 'Left_Foot', 'foot.L', 'FootL', 'LeftToeBase'];
              const rightFootNames = ['RightFoot', 'mixamorig:RightFoot', 'Right_Foot', 'foot.R', 'FootR', 'RightToeBase'];
              const leftFootBone = tempSkeleton.bones.find(b => 
                leftFootNames.some(name => b.name.toLowerCase().includes(name.toLowerCase()))
              );
              const rightFootBone = tempSkeleton.bones.find(b => 
                rightFootNames.some(name => b.name.toLowerCase().includes(name.toLowerCase()))
              );
              console.log(`[FootGrounding] Found foot bones: left=${leftFootBone?.name}, right=${rightFootBone?.name}`);
              
              // Animation name settings
              const animationNames = {
                walk: 'Walk Cycle', run: 'Run Cycle', jump: 'Jump', punch: 'Punch',
                kick: 'Kick', jab: 'Jab', block: 'Block', combo: 'Combo',
                idle: 'Idle', wave: 'Wave', dance: 'Dance'
              };
              const animName = animationNames[animationType] || 'Animation';
              
              // Stationary animations don't need root motion
              const isStationary = ['jump', 'block', 'idle', 'wave', 'dance', 'jab', 'punch', 'combo'].includes(animationType);
              
              // ========== PHASE 1: SAMPLE HIP POSITIONS AND FOOT GROUNDING AT EVERY FRAME ==========
              // This tells us exactly how the character moves through space in the animation
              // AND calculates the Y offset needed to keep the lowest foot grounded
              const hipPositions = [];
              const groundOffsets = [];  // Y offset to keep lowest foot at ground level
              let firstHipPos = null;
              
              for (let frame = 0; frame <= totalFrames; frame++) {
                const time = (frame / totalFrames) * duration;
                tempMixer.setTime(time);
                tempMixer.update(0);
                
                // Get hip position for XZ root motion
                if (hipBone) {
                  const worldPos = new THREE.Vector3();
                  hipBone.getWorldPosition(worldPos);
                  
                  if (frame === 0) {
                    firstHipPos = worldPos.clone();
                  }
                  
                  // Store delta from start position
                  hipPositions.push({
                    frame,
                    x: worldPos.x - firstHipPos.x,
                    y: worldPos.y - firstHipPos.y,
                    z: worldPos.z - firstHipPos.z
                  });
                }
                
                // Get foot positions for ground offset calculation
                // The lowest foot should be at Y=0, so character Y = -lowestFootY
                let lowestFootY = 0;
                if (leftFootBone || rightFootBone) {
                  const leftFootPos = new THREE.Vector3();
                  const rightFootPos = new THREE.Vector3();
                  
                  if (leftFootBone) leftFootBone.getWorldPosition(leftFootPos);
                  if (rightFootBone) rightFootBone.getWorldPosition(rightFootPos);
                  
                  if (leftFootBone && rightFootBone) {
                    lowestFootY = Math.min(leftFootPos.y, rightFootPos.y);
                  } else if (leftFootBone) {
                    lowestFootY = leftFootPos.y;
                  } else if (rightFootBone) {
                    lowestFootY = rightFootPos.y;
                  }
                }
                
                // Ground offset: to keep lowest foot at Y=0, character must move by -lowestFootY
                groundOffsets.push(-lowestFootY);
              }
              
              console.log(`[FootGrounding] Extracted ${groundOffsets.length} ground offsets, range: [${Math.min(...groundOffsets).toFixed(3)}, ${Math.max(...groundOffsets).toFixed(3)}]`);
              
              // Calculate total distance traveled (for locomotion animations)
              const lastPos = hipPositions[hipPositions.length - 1] || { x: 0, z: 0 };
              const totalDistance = Math.sqrt(lastPos.x * lastPos.x + lastPos.z * lastPos.z);
              const movementDirection = totalDistance > 0.01 
                ? { x: lastPos.x / totalDistance, z: lastPos.z / totalDistance }
                : { x: 0, z: 1 };
              
              console.log(`[TrueRootMotion] Hip movement: totalDistance=${totalDistance.toFixed(3)}, direction=(${movementDirection.x.toFixed(2)}, ${movementDirection.z.toFixed(2)})`);
              
              // ========== PHASE 2: EXTRACT KEYFRAMES (BONE ROTATIONS) ==========
              // Sample keyframes at regular intervals for smooth animation
              const keyframes = [];
              const sampleInterval = Math.max(1, Math.floor(totalFrames / 12)); // More samples = smoother
              
              for (let frame = 0; frame < totalFrames; frame += sampleInterval) {
                const time = (frame / totalFrames) * duration;
                tempMixer.setTime(time);
                tempMixer.update(0);
                
                const bones = {};
                tempSkeleton.bones.forEach((bone) => {
                  // Store rotation (quaternion) for all bones
                  bones[bone.name] = {
                    rotation: [
                      bone.quaternion.x,
                      bone.quaternion.y,
                      bone.quaternion.z,
                      bone.quaternion.w
                    ]
                  };
                });
                
                keyframes.push({
                  id: `extracted-${frame}`,
                  frame: frame,
                  bones: bones
                });
              }
              
              // Add final keyframe at exactly totalFrames for perfect looping
              // This keyframe has the same pose as frame 0 (animation loops back)
              // Without this, interpolation would blend from the last sampled frame
              // (e.g., frame 44) directly to frame 0, causing a jump
              const loopKeyframeBones = {};
              tempMixer.setTime(0); // Frame 48 = Frame 0 pose in a loop
              tempMixer.update(0);
              tempSkeleton.bones.forEach((bone) => {
                loopKeyframeBones[bone.name] = {
                  rotation: [
                    bone.quaternion.x,
                    bone.quaternion.y,
                    bone.quaternion.z,
                    bone.quaternion.w
                  ]
                };
              });
              keyframes.push({
                id: `extracted-${totalFrames}`,
                frame: totalFrames, // Frame 48 for a 48-frame animation
                bones: loopKeyframeBones
              });
              
              // ========== PHASE 3: BUILD ROOT MOTION TRACK ==========
              // This is the "true" root motion - extracted directly from the animation
              const rootMotionTrack = {
                enabled: !isStationary && totalDistance > 0.05,
                positions: hipPositions,
                groundOffsets: groundOffsets,  // NEW: Y offset to keep lowest foot grounded
                totalDistance: totalDistance,
                direction: movementDirection,
                // For convenience, also store interpolation-ready version
                getPositionAtFrame: (frame) => {
                  const clampedFrame = Math.max(0, Math.min(frame, totalFrames));
                  const floorFrame = Math.floor(clampedFrame);
                  const ceilFrame = Math.min(floorFrame + 1, totalFrames);
                  const t = clampedFrame - floorFrame;
                  
                  const p1 = hipPositions[floorFrame] || { x: 0, y: 0, z: 0 };
                  const p2 = hipPositions[ceilFrame] || p1;
                  
                  return {
                    x: p1.x + (p2.x - p1.x) * t,
                    y: p1.y + (p2.y - p1.y) * t,
                    z: p1.z + (p2.z - p1.z) * t
                  };
                }
              };
              
              console.log(`[TrueRootMotion] Extracted ${keyframes.length} keyframes, rootMotion.enabled=${rootMotionTrack.enabled}`);
              
              resolve({
                name: `${animName}`,
                description: `${animName} animation from Meshy`,
                fps: fps,
                total_frames: totalFrames,
                keyframes: keyframes,
                // New: True root motion track with actual animation movement
                rootMotionTrack: rootMotionTrack,
                // Legacy: Keep old format for backward compatibility
                rootMotion: {
                  enabled: rootMotionTrack.enabled,
                  distancePerCycle: totalDistance,
                  direction: movementDirection,
                  framePositions: hipPositions,
                  groundOffsets: groundOffsets  // Also add to legacy format
                },
                animationType: animationType
              });
              
            } catch (err) {
              console.error('[TrueRootMotion] Error extracting animation:', err);
              reject(err);
            }
          },
          (progress) => {
            // Loading progress
          },
          (error) => {
            console.error('[TrueRootMotion] Error loading animation GLB:', error);
            reject(error);
          }
        );
      });
    },
    
    // Sample bone positions for root motion analysis
    // Returns Hip, LeftFoot, RightFoot world positions at each frame
    sampleRootMotionData: (totalFrames = 48) => {
      if (!mixerRef.current || actionsRef.current.length === 0) {
        console.warn('No animations loaded to sample');
        return null;
      }
      
      if (!skeletonRef.current || !skeletonRef.current.bones) {
        console.warn('No skeleton loaded');
        return null;
      }
      
      const action = actionsRef.current[0];
      const clip = action.getClip();
      if (!clip) return null;
      
      const duration = clip.duration;
      
      // Find required bones
      const findBone = (names) => {
        return skeletonRef.current.bones.find(b => 
          names.some(name => b.name.toLowerCase().includes(name.toLowerCase()))
        );
      };
      
      const hipBone = findBone(['hips', 'pelvis', 'root']);
      const leftFootBone = findBone(['leftfoot', 'left_foot', 'l_foot', 'foot_l', 'foot.l']);
      const rightFootBone = findBone(['rightfoot', 'right_foot', 'r_foot', 'foot_r', 'foot.r']);
      
      console.log('Root motion sampling - found bones:', {
        hip: hipBone?.name,
        leftFoot: leftFootBone?.name,
        rightFoot: rightFootBone?.name
      });
      
      if (!hipBone || !leftFootBone || !rightFootBone) {
        console.warn('Missing required bones for root motion analysis');
        return null;
      }
      
      // Save current animation state
      const wasPlaying = action.isRunning();
      const wasPaused = action.paused;
      action.paused = false;
      action.play();
      
      // Sample positions at each frame
      const hipPositions = [];
      const leftFootPositions = [];
      const rightFootPositions = [];
      
      let firstHipPos = null;
      
      for (let frame = 0; frame < totalFrames; frame++) {
        const time = (frame / totalFrames) * duration;
        mixerRef.current.setTime(time);
        mixerRef.current.update(0);
        
        // Get world positions
        const hipWorld = new THREE.Vector3();
        const leftFootWorld = new THREE.Vector3();
        const rightFootWorld = new THREE.Vector3();
        
        hipBone.getWorldPosition(hipWorld);
        leftFootBone.getWorldPosition(leftFootWorld);
        rightFootBone.getWorldPosition(rightFootWorld);
        
        // Store first hip position as origin
        if (frame === 0) {
          firstHipPos = hipWorld.clone();
        }
        
        // Store positions (hip relative to start, feet in world space)
        hipPositions.push({
          frame,
          x: hipWorld.x - firstHipPos.x,
          y: hipWorld.y - firstHipPos.y,
          z: hipWorld.z - firstHipPos.z
        });
        
        leftFootPositions.push({
          frame,
          position: {
            x: leftFootWorld.x,
            y: leftFootWorld.y,
            z: leftFootWorld.z
          }
        });
        
        rightFootPositions.push({
          frame,
          position: {
            x: rightFootWorld.x,
            y: rightFootWorld.y,
            z: rightFootWorld.z
          }
        });
      }
      
      // Reset mixer and restore action state
      mixerRef.current.setTime(0);
      mixerRef.current.update(0);
      action.paused = wasPaused;
      if (!wasPlaying) {
        action.stop();
      }
      
      console.log(`Sampled ${totalFrames} frames for root motion analysis`);
      
      return {
        hipPositions,
        leftFootPositions,
        rightFootPositions,
        totalFrames,
        hipBoneName: hipBone.name,
        leftFootBoneName: leftFootBone.name,
        rightFootBoneName: rightFootBone.name
      };
    },
    
    // Enable/disable foot IK locking
    setFootLocking: (enabled, contactData) => {
      enableFootLockingRef.current = enabled;
      footContactDataRef.current = contactData;
      
      if (!enabled) {
        // Reset plant positions when disabled
        leftFootPlantPosRef.current = null;
        rightFootPlantPosRef.current = null;
      }
      
      console.log('Foot IK locking:', enabled ? 'ENABLED' : 'DISABLED');
    },
    
    // Update foot IK for current frame (call during playback)
    updateFootIKForFrame: (frame, totalFrames) => {
      updateFootIK(frame, totalFrames);
    },
    
    // Get foot bone positions (for debugging)
    getFootPositions: () => {
      const leftPos = new THREE.Vector3();
      const rightPos = new THREE.Vector3();
      
      if (leftFootBoneRef.current) {
        leftFootBoneRef.current.getWorldPosition(leftPos);
      }
      if (rightFootBoneRef.current) {
        rightFootBoneRef.current.getWorldPosition(rightPos);
      }
      
      return {
        left: { x: leftPos.x, y: leftPos.y, z: leftPos.z },
        right: { x: rightPos.x, y: rightPos.y, z: rightPos.z },
        leftPlanted: leftFootPlantPosRef.current ? { ...leftFootPlantPosRef.current } : null,
        rightPlanted: rightFootPlantPosRef.current ? { ...rightFootPlantPosRef.current } : null
      };
    }
  }), [updateFootIK]);

  // Initialize scene
  useEffect(() => {
    if (!containerRef.current) return;
    
    const container = containerRef.current;
    const width = container.clientWidth;
    const height = container.clientHeight;

    // Scene
    const scene = new THREE.Scene();
    const bgColor = backgroundColor === 'transparent' ? null : new THREE.Color(backgroundColor);
    scene.background = bgColor;
    sceneRef.current = scene;

    // Camera
    const camera = new THREE.PerspectiveCamera(fov, width / height, 0.1, 1000);
    camera.position.set(0, 1.5, distance);
    cameraRef.current = camera;

    // Renderer
    const renderer = new THREE.WebGLRenderer({ 
      antialias: true,
      alpha: backgroundColor === 'transparent'
    });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    // Add tone mapping for better color reproduction
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.2;
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Controls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.target.set(0, targetY, 0);
    controls.minDistance = 0.5;
    controls.maxDistance = 20;
    // Disable default OrbitControls behavior - we'll handle camera manually
    controls.enableRotate = false;
    controls.enablePan = false;
    controls.enableZoom = false;
    controls.update();
    controlsRef.current = controls;
    
    // Custom camera mouse controls - use refs to get current callback (avoids stale closures)
    const handleMouseDown = (e) => {
      if (!onCameraChangeRef.current) return;
      
      const isMiddleButton = e.button === 1;
      const isRightButton = e.button === 2;
      const isLeftButton = e.button === 0;
      
      // Determine drag mode based on button and modifiers
      if (isMiddleButton || (isLeftButton && e.shiftKey)) {
        // Middle-drag or Shift+Left-drag = Pan (X/Y)
        dragModeRef.current = 'pan';
        isDraggingRef.current = true;
      } else if (isRightButton || (isLeftButton && e.altKey)) {
        if (e.ctrlKey || e.metaKey) {
          // Ctrl+Right-drag = Roll
          dragModeRef.current = 'roll';
        } else {
          // Right-drag or Alt+Left-drag = Orbit (Pitch/Yaw)
          dragModeRef.current = 'orbit';
        }
        isDraggingRef.current = true;
      }
      
      if (isDraggingRef.current) {
        lastMousePosRef.current = { x: e.clientX, y: e.clientY };
        e.preventDefault();
      }
    };
    
    const handleMouseMove = (e) => {
      if (!isDraggingRef.current || !onCameraChangeRef.current) return;
      
      const deltaX = e.clientX - lastMousePosRef.current.x;
      const deltaY = e.clientY - lastMousePosRef.current.y;
      lastMousePosRef.current = { x: e.clientX, y: e.clientY };
      
      const current = cameraSettingsRef.current || {
        position: { x: 0, y: 2, z: 5 },
        rotation: { x: -15, y: 0, z: 0 }
      };
      
      const sensitivity = {
        pan: 0.01,
        orbit: 0.3,
        roll: 0.3
      };
      
      if (dragModeRef.current === 'pan') {
        // Pan: move camera X/Y position
        const newSettings = {
          ...current,
          position: {
            ...current.position,
            x: (current.position?.x || 0) - deltaX * sensitivity.pan,
            y: (current.position?.y || 2) + deltaY * sensitivity.pan
          }
        };
        onCameraChangeRef.current(newSettings);
      } else if (dragModeRef.current === 'orbit') {
        // Orbit: adjust Pitch (vertical) and Yaw (horizontal)
        const newSettings = {
          ...current,
          rotation: {
            ...current.rotation,
            x: Math.max(-89, Math.min(89, (current.rotation?.x || 0) + deltaY * sensitivity.orbit)),
            y: (current.rotation?.y || 0) - deltaX * sensitivity.orbit
          }
        };
        onCameraChangeRef.current(newSettings);
      } else if (dragModeRef.current === 'roll') {
        // Roll: adjust Z rotation
        const newSettings = {
          ...current,
          rotation: {
            ...current.rotation,
            z: (current.rotation?.z || 0) + deltaX * sensitivity.roll
          }
        };
        onCameraChangeRef.current(newSettings);
      }
    };
    
    const handleMouseUp = () => {
      isDraggingRef.current = false;
      dragModeRef.current = null;
    };
    
    const handleWheel = (e) => {
      if (!onCameraChangeRef.current) return;
      
      e.preventDefault();
      
      const current = cameraSettingsRef.current || {
        position: { x: 0, y: 2, z: 5 },
        rotation: { x: -15, y: 0, z: 0 }
      };
      
      // Scroll = Zoom (Z position)
      const zoomSensitivity = 0.002;
      const deltaZ = e.deltaY * zoomSensitivity;
      const newZ = Math.max(0.5, Math.min(20, (current.position?.z || 5) + deltaZ));
      
      const newSettings = {
        ...current,
        position: {
          ...current.position,
          z: newZ
        }
      };
      onCameraChangeRef.current(newSettings);
    };
    
    const handleContextMenu = (e) => {
      e.preventDefault(); // Prevent context menu on right-click
    };
    
    // Add event listeners
    renderer.domElement.addEventListener('mousedown', handleMouseDown);
    renderer.domElement.addEventListener('mousemove', handleMouseMove);
    renderer.domElement.addEventListener('mouseup', handleMouseUp);
    renderer.domElement.addEventListener('mouseleave', handleMouseUp);
    renderer.domElement.addEventListener('wheel', handleWheel, { passive: false });
    renderer.domElement.addEventListener('contextmenu', handleContextMenu);

    // Transform controls for bone manipulation
    const transformControls = new TransformControls(camera, renderer.domElement);
    transformControls.setMode('rotate');
    transformControls.setSpace('local'); // Use local space for bone rotation
    transformControlsRef.current = transformControls;
    
    // In Three.js r182+, we need to add the helper, not the controls directly
    // The helper contains the visual gizmo elements
    const transformHelper = transformControls.getHelper();
    scene.add(transformHelper);
    
    // Disable orbit controls when using transform controls
    transformControls.addEventListener('dragging-changed', (event) => {
      controls.enabled = !event.value;
    });
    
    // Handle transform changes - notify parent of bone rotation
    transformControls.addEventListener('objectChange', () => {
      if (transformControls.object && onBoneTransformChange) {
        const bone = transformControls.object;
        onBoneTransformChange(bone.name, {
          rotation: [bone.quaternion.x, bone.quaternion.y, bone.quaternion.z, bone.quaternion.w],
          position: [bone.position.x, bone.position.y, bone.position.z]
        });
      }
    });

    // Lighting - optimized for displaying textured models
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.8);  // Increased for better texture visibility
    scene.add(ambientLight);

    const mainLight = new THREE.DirectionalLight(0xffffff, 1.0);  // Slightly reduced to avoid washing out
    mainLight.position.set(5, 10, 5);
    mainLight.castShadow = true;
    mainLight.shadow.mapSize.width = 2048;
    mainLight.shadow.mapSize.height = 2048;
    mainLight.shadow.camera.near = 0.5;
    mainLight.shadow.camera.far = 50;
    mainLight.shadow.camera.left = -10;
    mainLight.shadow.camera.right = 10;
    mainLight.shadow.camera.top = 10;
    mainLight.shadow.camera.bottom = -10;
    scene.add(mainLight);
    mainLightRef.current = mainLight; // Store ref for keyframe control

    const fillLight = new THREE.DirectionalLight(0xffffff, 0.5);
    fillLight.position.set(-5, 5, -5);
    scene.add(fillLight);

    // Back light to bring out silhouette
    const backLight = new THREE.DirectionalLight(0xffffff, 0.3);
    backLight.position.set(0, 5, -10);
    scene.add(backLight);

    const hemisphereLight = new THREE.HemisphereLight(0xffffff, 0x444444, 0.6);
    scene.add(hemisphereLight);

    // Ground for shadows
    const groundGeometry = new THREE.PlaneGeometry(20, 20);
    const groundMaterial = new THREE.ShadowMaterial({ opacity: 0.3 });
    const ground = new THREE.Mesh(groundGeometry, groundMaterial);
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = 0;
    ground.receiveShadow = true;
    scene.add(ground);

    // Animation loop (basic Three.js animations only)
    const animate = () => {
      animationIdRef.current = requestAnimationFrame(animate);
      
      const delta = clockRef.current.getDelta();
      const now = performance.now();
      
      // Only play built-in animations if NOT using keyframe animation
      // Use refs to get current values (props are stale in this closure)
      if (mixerRef.current && isPlayingRef.current && useBuiltInAnimationRef.current && !useKeyframeAnimationRef.current) {
        mixerRef.current.update(delta * animationSpeed);
      }
      
      // Report bone transforms for 2D sprite rendering (throttled to ~30fps for performance)
      if (onBoneDataUpdateRef.current && Object.keys(bonesRef.current).length > 0 && (now - lastBoneUpdateTimeRef.current > 33)) {
        lastBoneUpdateTimeRef.current = now;
        
        const boneData = {};
        const euler = new THREE.Euler();
        
        Object.entries(bonesRef.current).forEach(([name, bone]) => {
          // Get world position and rotation
          const worldPos = new THREE.Vector3();
          const worldQuat = new THREE.Quaternion();
          bone.getWorldPosition(worldPos);
          bone.getWorldQuaternion(worldQuat);
          
          // Convert quaternion to Euler angles
          euler.setFromQuaternion(worldQuat);
          
          boneData[name] = {
            position: { x: worldPos.x, y: worldPos.y, z: worldPos.z },
            rotation: { x: euler.x, y: euler.y, z: euler.z },
            quaternion: { x: worldQuat.x, y: worldQuat.y, z: worldQuat.z, w: worldQuat.w }
          };
        });
        
        onBoneDataUpdateRef.current(boneData);
      }
      
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    // Handle click for bone selection - use ref to get current value
    const handleClick = (event) => {
      // Check refs for current posing state
      if (!modelRef.current) return;
      
      const rect = renderer.domElement.getBoundingClientRect();
      mouseRef.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouseRef.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      
      raycasterRef.current.setFromCamera(mouseRef.current, camera);
      
      // Check intersections with bone helpers first
      if (boneHelpersRef.current.length > 0) {
        const helperIntersects = raycasterRef.current.intersectObjects(boneHelpersRef.current, false);
        
        if (helperIntersects.length > 0) {
          const clickedHelper = helperIntersects[0].object;
          const boneName = clickedHelper.userData?.boneName;
          if (boneName && onBoneSelect) {
            console.log('Clicked bone helper:', boneName);
            onBoneSelect(boneName);
            return;
          }
        }
      }
      
      // Fallback: Check mesh intersections and find nearest bone
      if (modelRef.current && Object.keys(bonesRef.current).length > 0) {
        const meshIntersects = raycasterRef.current.intersectObject(modelRef.current, true);
        
        if (meshIntersects.length > 0) {
          const point = meshIntersects[0].point;
          
          // Find the closest bone to the click point
          let closestBone = null;
          let closestDist = Infinity;
          
          Object.entries(bonesRef.current).forEach(([name, bone]) => {
            const boneWorldPos = new THREE.Vector3();
            bone.getWorldPosition(boneWorldPos);
            const dist = point.distanceTo(boneWorldPos);
            
            if (dist < closestDist) {
              closestDist = dist;
              closestBone = name;
            }
          });
          
          if (closestBone && closestDist < 0.5 && onBoneSelect) {
            console.log('Clicked near bone:', closestBone);
            onBoneSelect(closestBone);
            return;
          }
        }
      }
      
      // If we got here, no bone was clicked - deselect current bone
      if (onBoneSelect) {
        console.log('Clicked empty space - deselecting bone');
        onBoneSelect(null);
      }
    };
    
    renderer.domElement.addEventListener('click', handleClick);

    // Handle resize
    const handleResize = () => {
      if (!container || !renderer || !camera) return;
      const w = container.clientWidth;
      const h = container.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    
    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(container);

    // Cleanup
    return () => {
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
      }
      renderer.domElement.removeEventListener('click', handleClick);
      renderer.domElement.removeEventListener('mousedown', handleMouseDown);
      renderer.domElement.removeEventListener('mousemove', handleMouseMove);
      renderer.domElement.removeEventListener('mouseup', handleMouseUp);
      renderer.domElement.removeEventListener('mouseleave', handleMouseUp);
      renderer.domElement.removeEventListener('wheel', handleWheel);
      renderer.domElement.removeEventListener('contextmenu', handleContextMenu);
      resizeObserver.disconnect();
      // Remove helper from scene before disposing
      if (scene.children.includes(transformHelper)) {
        scene.remove(transformHelper);
      }
      transformControls.dispose();
      renderer.dispose();
      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
      // Dispose of model
      if (modelRef.current) {
        modelRef.current.traverse((child) => {
          if (child.geometry) child.geometry.dispose();
          if (child.material) {
            if (Array.isArray(child.material)) {
              child.material.forEach(m => m.dispose());
            } else {
              child.material.dispose();
            }
          }
        });
      }
    };
  }, [backgroundColor, enablePosing]); // Note: onCameraChange is accessed via ref to avoid scene recreation

  // Track current loading URL to prevent stale loads
  const loadingUrlRef = useRef(null);
  const isLoadingRef = useRef(false);

  // Load model when URL changes
  useEffect(() => {
    if (!modelUrl || !sceneRef.current) return;
    
    // Skip if already loading the same URL
    if (loadingUrlRef.current === modelUrl) {
      return;
    }
    
    // Mark this URL as currently loading
    const currentLoadUrl = modelUrl;
    loadingUrlRef.current = currentLoadUrl;
    isLoadingRef.current = true;

    // Clear existing model from scene
    const clearScene = () => {
      if (modelRef.current) {
        sceneRef.current.remove(modelRef.current);
        modelRef.current.traverse((child) => {
          if (child.geometry) child.geometry.dispose();
          if (child.material) {
            if (Array.isArray(child.material)) {
              child.material.forEach(m => m.dispose());
            } else {
              child.material.dispose();
            }
          }
        });
        modelRef.current = null;
      }
      if (mixerRef.current) {
        mixerRef.current.stopAllAction();
        mixerRef.current = null;
      }
      actionsRef.current = [];
    };
    
    clearScene();
    
    // Use a microtask to batch the state updates
    queueMicrotask(() => {
      if (loadingUrlRef.current === currentLoadUrl) {
        setLoading(true);
        setError(null);
      }
    });

    const loader = new GLTFLoader();
    
    loader.load(
      modelUrl,
      (gltf) => {
        // CRITICAL: Check if this load is still valid (URL hasn't changed)
        if (loadingUrlRef.current !== currentLoadUrl) {
          console.log('Model load completed but URL changed, discarding:', currentLoadUrl.slice(-40));
          // Dispose of the loaded model since we don't need it
          gltf.scene.traverse((child) => {
            if (child.geometry) child.geometry.dispose();
            if (child.material) {
              if (Array.isArray(child.material)) {
                child.material.forEach(m => m.dispose());
              } else {
                child.material.dispose();
              }
            }
          });
          return;
        }
        
        const model = gltf.scene;
        
        // Debug: Log material info to help diagnose texture issues
        console.log('Model loaded, processing materials...');
        
        // Process materials for proper rendering
        model.traverse((child) => {
          if (child.isMesh) {
            child.castShadow = true;
            child.receiveShadow = true;
            
            // Fix material rendering issues
            const materials = Array.isArray(child.material) ? child.material : [child.material];
            materials.forEach((material, idx) => {
              if (material) {
                // Debug: Log material properties before modification
                console.log(`Processing material ${idx}: type=${material.type}, hasMap=${!!material.map}, metalness=${material.metalness}, roughness=${material.roughness}`);
                
                // Ensure textures use correct color space
                if (material.map) {
                  material.map.colorSpace = THREE.SRGBColorSpace;
                  material.map.needsUpdate = true;
                  console.log(`  - Applied SRGB colorspace to texture map`);
                }
                
                // Fix for grey/washed out appearance - adjust material properties
                // Some GLTF exports have incorrect metalness/roughness making things look grey
                if (material.isMeshStandardMaterial || material.isMeshPhysicalMaterial) {
                  // If metalness is very high and there's no metalness map, reduce it
                  // This prevents the "grey metallic" look on non-metallic surfaces
                  if (material.metalness > 0.5 && !material.metalnessMap) {
                    console.log(`  - Reducing metalness from ${material.metalness} to 0.0`);
                    material.metalness = 0.0;
                  }
                  // Ensure roughness isn't too extreme
                  if (material.roughness > 0.95) {
                    console.log(`  - Reducing roughness from ${material.roughness} to 0.8`);
                    material.roughness = 0.8;
                  }
                  // Make sure the base color isn't being overridden
                  if (material.color && material.map) {
                    // Reset color to white to let the texture show through properly
                    material.color.setRGB(1, 1, 1);
                  }
                }
                
                // Handle emissive map color space
                if (material.emissiveMap) {
                  material.emissiveMap.colorSpace = THREE.SRGBColorSpace;
                  material.emissiveMap.needsUpdate = true;
                }
                
                // Ensure proper normal map settings
                if (material.normalMap) {
                  material.normalMap.colorSpace = THREE.LinearSRGBColorSpace;
                }
                
                material.needsUpdate = true;
              }
            });
          }
        });

        // Center the model
        const box = new THREE.Box3().setFromObject(model);
        const center = box.getCenter(new THREE.Vector3());
        model.position.y = -box.min.y; // Feet at ground
        model.position.x = -center.x;
        model.position.z = -center.z;

        // Apply scale and rotation
        model.scale.setScalar(scale);
        model.rotation.y = THREE.MathUtils.degToRad(rotation);

        // Helper function to apply textures from source model to target model
        const applyTexturesFromSource = (sourceScene, targetModel) => {
          // Extract texture from source
          let sourceTexture = null;
          let sourceMaterial = null;
          
          sourceScene.traverse((child) => {
            if (child.isMesh && child.material) {
              const mat = Array.isArray(child.material) ? child.material[0] : child.material;
              if (mat && mat.map) {
                sourceTexture = mat.map;
                sourceMaterial = mat;
                console.log('Found texture in source model');
              }
            }
          });
          
          if (sourceTexture) {
            // Apply texture to target model
            targetModel.traverse((child) => {
              if (child.isMesh) {
                const materials = Array.isArray(child.material) ? child.material : [child.material];
                materials.forEach((material) => {
                  if (material && material.isMeshStandardMaterial) {
                    // Clone the texture so each material has its own reference
                    material.map = sourceTexture.clone();
                    material.map.colorSpace = THREE.SRGBColorSpace;
                    material.map.needsUpdate = true;
                    material.metalness = 0;
                    material.roughness = 0.8;
                    material.color.setRGB(1, 1, 1);
                    material.needsUpdate = true;
                    console.log('Applied texture to armature model');
                  }
                });
              }
            });
          }
        };
        
        // Check if model needs textures from textured model
        const needsTextures = !model.children.some(child => {
          if (child.isMesh && child.material) {
            const mat = Array.isArray(child.material) ? child.material[0] : child.material;
            return mat && mat.map;
          }
          return false;
        }) && !!texturedModelUrl;
        
        // Function to finish model setup (called after textures are loaded or immediately)
        const finishModelSetup = () => {
          // Clear scene one more time to be safe (in case another model snuck in)
          clearScene();
          
          // Clear old bone helpers
          boneHelpersRef.current.forEach(helper => {
            if (helper.parent) helper.parent.remove(helper);
            if (helper.geometry) helper.geometry.dispose();
            if (helper.material) helper.material.dispose();
          });
          boneHelpersRef.current = [];
          bonesRef.current = {};
          
          sceneRef.current.add(model);
          modelRef.current = model;
          
          continueModelSetup();
        };
        
        // Continue with the rest of the setup (skeleton, bones, animations)
        const continueModelSetup = () => {
          // Extract skeleton and bones
        let skeleton = null;
        const boneNames = [];
        
        model.traverse((child) => {
          if (child.isSkinnedMesh && child.skeleton) {
            skeleton = child.skeleton;
            skeletonRef.current = skeleton;
          }
          if (child.isBone) {
            bonesRef.current[child.name] = child;
            boneNames.push(child.name);
            
            // Create clickable bone helper (sphere at joint)
            // Size varies based on bone importance
            const isMainBone = ['Hips', 'Spine', 'Head', 'LeftHand', 'RightHand', 'LeftFoot', 'RightFoot'].some(
              name => child.name.includes(name)
            );
            const helperSize = isMainBone ? 0.05 : 0.03;
            
            const helperGeometry = new THREE.SphereGeometry(helperSize, 12, 12);
            const helperMaterial = new THREE.MeshBasicMaterial({ 
              color: 0x00ffff,
              transparent: true,
              opacity: 0.7,
              depthTest: false,
              depthWrite: false
            });
            const helper = new THREE.Mesh(helperGeometry, helperMaterial);
            helper.userData.boneName = child.name;
            helper.userData.isBoneHelper = true;
            helper.renderOrder = 999;
            helper.layers.enable(1); // Put on layer 1 for raycasting
            child.add(helper);
            boneHelpersRef.current.push(helper);
          }
        });
        
        // Notify parent of available bones
        if (onBonesLoaded && boneNames.length > 0) {
          onBonesLoaded(boneNames);
        }

        // ====== SETUP FOOT IK SOLVER ======
        // Find the skinned mesh and leg bones for IK
        model.traverse((child) => {
          if (child.isSkinnedMesh && child.skeleton) {
            skinnedMeshRef.current = child;
          }
        });
        
        if (skeleton && skinnedMeshRef.current) {
          setupFootIK(skeleton, skinnedMeshRef.current, sceneRef.current);
        }

        // Setup animations
        if (gltf.animations && gltf.animations.length > 0) {
          mixerRef.current = new THREE.AnimationMixer(model);
          
          const animNames = gltf.animations.map(clip => clip.name);
          if (onAnimationsLoaded) {
            onAnimationsLoaded(animNames);
          }

          // Store animations but DON'T auto-play them
          // The timeline/keyframe system controls all animation playback
          const firstClip = gltf.animations[0];
          const action = mixerRef.current.clipAction(firstClip);
          action.paused = true; // Start paused - timeline controls playback
          action.play(); // Need to call play() to register, but it's paused
          actionsRef.current.push(action);
        }

        setLoading(false);
        }; // End of continueModelSetup
        
        // If model needs textures and we have a texturedModelUrl, load it
        if (needsTextures) {
          console.log('Model needs textures, loading from:', texturedModelUrl);
          const textureLoader = new GLTFLoader();
          textureLoader.load(
            texturedModelUrl,
            (texturedGltf) => {
              console.log('Textured model loaded, applying textures...');
              applyTexturesFromSource(texturedGltf.scene, model);
              // Dispose of the textured model since we only needed its textures
              texturedGltf.scene.traverse((child) => {
                if (child.geometry) child.geometry.dispose();
                // Don't dispose materials/textures since we're using them
              });
              finishModelSetup();
            },
            undefined,
            (err) => {
              console.warn('Failed to load textured model:', err);
              // Continue without textures
              finishModelSetup();
            }
          );
        } else {
          // No textures needed, continue directly
          finishModelSetup();
        }
      },
      (progress) => {
        // Progress callback
      },
      (err) => {
        // Only show error if this load is still relevant
        if (loadingUrlRef.current === currentLoadUrl) {
          console.error('Failed to load model:', err);
          setError('Failed to load 3D model');
          setLoading(false);
        }
      }
    );
    
    // Cleanup on unmount or URL change
    return () => {
      // This will cause in-flight loads to be discarded when they complete
    };
  }, [modelUrl, texturedModelUrl]); // Re-run when URL changes or textured model URL changes

  // Update camera position when orbit params change
  useEffect(() => {
    if (!cameraRef.current || !controlsRef.current) return;
    
    // Skip orbit-based camera control if cameraSettings has non-zero yaw
    // This allows the keyframed camera settings to control the view angle
    const hasCustomYaw = cameraSettings?.rotation?.y && Math.abs(cameraSettings.rotation.y) > 0.1;
    if (hasCustomYaw) {
      // cameraSettings is controlling the camera angle, skip orbit positioning
      return;
    }
    
    const camera = cameraRef.current;
    const controls = controlsRef.current;
    
    // Convert spherical to cartesian
    const theta = THREE.MathUtils.degToRad(orbitTheta);
    const phi = THREE.MathUtils.degToRad(orbitPhi);
    
    const x = distance * Math.sin(phi) * Math.sin(theta);
    const y = targetY + distance * Math.cos(phi);
    const z = distance * Math.sin(phi) * Math.cos(theta);
    
    camera.position.set(x, y, z);
    camera.fov = fov;
    camera.updateProjectionMatrix();
    
    controls.target.set(0, targetY, 0);
    controls.update();
  }, [orbitTheta, orbitPhi, distance, fov, targetY, cameraSettings]);

  // Update refs when props change (for animation loop to access current values)
  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);
  
  useEffect(() => {
    useBuiltInAnimationRef.current = useBuiltInAnimation;
  }, [useBuiltInAnimation]);
  
  useEffect(() => {
    useKeyframeAnimationRef.current = useKeyframeAnimation;
  }, [useKeyframeAnimation]);

  // Sync foot locking props to refs
  useEffect(() => {
    enableFootLockingRef.current = enableFootLocking;
    // Reset plant positions when foot locking is toggled
    if (!enableFootLocking) {
      leftFootPlantPosRef.current = null;
      rightFootPlantPosRef.current = null;
    }
  }, [enableFootLocking]);
  
  useEffect(() => {
    footContactDataRef.current = footContactData;
  }, [footContactData]);

  // Built-in GLTF animations - play when useBuiltInAnimation is true AND NOT using keyframe animation
  useEffect(() => {
    actionsRef.current.forEach(action => {
      // Play built-in animation only when:
      // 1. isPlaying is true
      // 2. useBuiltInAnimation is true
      // 3. useKeyframeAnimation is false (keyframe animation takes priority)
      const shouldPlay = isPlaying && useBuiltInAnimation && !useKeyframeAnimation;
      action.paused = !shouldPlay;
      action.timeScale = animationSpeed;
      
      // If using keyframe animation, stop the mixer completely to prevent interference
      if (useKeyframeAnimation && mixerRef.current) {
        mixerRef.current.stopAllAction();
      }
    });
  }, [isPlaying, animationSpeed, useBuiltInAnimation, useKeyframeAnimation]);

  // Keyframe animation playback
  // Apply keyframe animation based on currentFrame from parent
  // Parent manages the playback loop and passes currentFrame (clipLocalFrame)
  useEffect(() => {
    if (keyframes.length === 0) {
      return;
    }
    
    // Apply pose for current frame using interpolation (with loop support)
    const pose = getInterpolatedPose(currentFrame, keyframes, totalFrames);
    if (pose && Object.keys(pose).length > 0) {
      applyBoneTransforms(pose);
    }
  }, [currentFrame, keyframes, totalFrames, getInterpolatedPose, applyBoneTransforms]);

  // Update foot IK for current frame when foot locking is enabled
  useEffect(() => {
    // Only attempt foot IK if everything is properly initialized
    if (!enableFootLocking || !footContactData) {
      return;
    }
    
    // Make sure the model is loaded and ready
    if (!modelRef.current || !skinnedMeshRef.current) {
      return;
    }
    
    // Make sure we have valid foot bones
    if (!leftFootBoneRef.current || !rightFootBoneRef.current) {
      return;
    }
    
    // Make sure we have valid contact data
    if (!footContactData.left || !footContactData.right || 
        footContactData.left.length === 0 || footContactData.right.length === 0) {
      return;
    }
    
    // Safely call updateFootIK
    try {
      updateFootIK(currentFrame, totalFrames);
    } catch (err) {
      console.error('Foot IK update error:', err);
    }
  }, [currentFrame, totalFrames, enableFootLocking, footContactData, updateFootIK]);

  // Update model transform
  useEffect(() => {
    if (!modelRef.current) return;
    modelRef.current.scale.setScalar(scale);
    modelRef.current.rotation.y = THREE.MathUtils.degToRad(rotation);
  }, [scale, rotation]);

  // Toggle grid
  useEffect(() => {
    if (!sceneRef.current) return;
    
    // Remove existing grid
    const existingGrid = sceneRef.current.getObjectByName('grid');
    if (existingGrid) {
      sceneRef.current.remove(existingGrid);
    }
    
    if (showGrid) {
      const gridHelper = new THREE.GridHelper(10, 20, 0x555555, 0x333333);
      gridHelper.name = 'grid';
      sceneRef.current.add(gridHelper);
    }
  }, [showGrid]);

  // Apply character world transforms (position, rotation, scale, visibility)
  useEffect(() => {
    if (!modelRef.current) return;
    
    const model = modelRef.current;
    
    // Apply position
    model.position.set(
      characterPosition.x,
      characterPosition.y,
      characterPosition.z
    );
    
    // Apply rotation (X, Y, Z - degrees to radians)
    model.rotation.x = THREE.MathUtils.degToRad(characterRotation.x || 0);
    model.rotation.y = THREE.MathUtils.degToRad(characterRotation.y || 0);
    model.rotation.z = THREE.MathUtils.degToRad(characterRotation.z || 0);
    
    // Apply scale (can be object {x,y,z} or number)
    if (typeof characterScale === 'number') {
      model.scale.setScalar(characterScale);
    } else {
      model.scale.set(
        characterScale.x || 1,
        characterScale.y || 1,
        characterScale.z || 1
      );
    }
    
    // Apply visibility
    model.visible = characterVisible;
    
    // Apply opacity to all materials
    model.traverse((child) => {
      if (child.isMesh && child.material) {
        const materials = Array.isArray(child.material) ? child.material : [child.material];
        materials.forEach(mat => {
          if (characterOpacity < 1) {
            mat.transparent = true;
            mat.opacity = characterOpacity;
            mat.needsUpdate = true;
          } else {
            mat.transparent = false;
            mat.opacity = 1;
            mat.needsUpdate = true;
          }
        });
      }
    });
    
  }, [characterPosition, characterRotation, characterScale, characterVisible, characterOpacity]);

  // Handle selected bone - attach transform controls
  // Apply camera and light settings from keyframes
  useEffect(() => {
    // Apply camera settings
    if (cameraSettings && cameraRef.current && controlsRef.current) {
      const camera = cameraRef.current;
      const controls = controlsRef.current;
      
      // The character is at the origin. Camera settings work as follows:
      // - Position: where the camera is relative to the character
      // - Rotation: additional rotation adjustments (pitch/yaw/roll)
      //   - Pitch (X): tilt up/down from horizontal
      //   - Yaw (Y): horizontal rotation around the character
      //   - Roll (Z): tilt sideways (rarely used)
      
      // Start with the base position from settings
      let camX = cameraSettings.position?.x ?? 0;
      let camY = cameraSettings.position?.y ?? 2;
      let camZ = cameraSettings.position?.z ?? 5;
      
      // Apply yaw rotation to camera position (orbit around Y axis)
      if (cameraSettings.rotation?.y) {
        const yaw = THREE.MathUtils.degToRad(cameraSettings.rotation.y);
        // Rotate the camera position around the Y axis (keeping Y height same)
        const radius = Math.sqrt(camX * camX + camZ * camZ);
        const currentAngle = Math.atan2(camX, camZ);
        const newAngle = currentAngle + yaw;
        camX = radius * Math.sin(newAngle);
        camZ = radius * Math.cos(newAngle);
      }
      
      // Set camera position
      camera.position.set(camX, camY, camZ);
      
      // Set target to look at the character (origin, at character height)
      const targetY = 1; // Look at character's center (roughly waist height)
      controls.target.set(0, targetY, 0);
      
      // Apply pitch by adjusting the target Y or camera Y offset
      if (cameraSettings.rotation?.x) {
        const pitch = THREE.MathUtils.degToRad(cameraSettings.rotation.x);
        // Pitch adjusts where we look - negative pitch looks down
        const lookDistance = Math.sqrt(camX * camX + camZ * camZ);
        const pitchOffset = lookDistance * Math.tan(pitch);
        controls.target.y = targetY + pitchOffset;
      }
      
      controls.update();
      
      // Apply roll by rotating the camera's up vector
      // Roll tilts the camera sideways (Dutch angle effect)
      if (cameraSettings.rotation?.z) {
        const roll = THREE.MathUtils.degToRad(cameraSettings.rotation.z);
        // Rotate the up vector around the camera's forward direction
        const forward = new THREE.Vector3().subVectors(controls.target, camera.position).normalize();
        const defaultUp = new THREE.Vector3(0, 1, 0);
        // Create rotation quaternion around forward axis
        const rollQuat = new THREE.Quaternion().setFromAxisAngle(forward, roll);
        // Apply roll to the up vector
        camera.up.copy(defaultUp).applyQuaternion(rollQuat);
        camera.lookAt(controls.target);
      } else {
        // Reset up vector to default when no roll
        camera.up.set(0, 1, 0);
      }
    }
    
    // Apply light settings
    if (lightSettings && mainLightRef.current) {
      const light = mainLightRef.current;
      
      // Set light position
      if (lightSettings.position) {
        light.position.set(
          lightSettings.position.x ?? light.position.x,
          lightSettings.position.y ?? light.position.y,
          lightSettings.position.z ?? light.position.z
        );
      }
      
      // Set light intensity
      if (lightSettings.intensity !== undefined) {
        light.intensity = lightSettings.intensity;
      }
    }
  }, [cameraSettings, lightSettings]);

  useEffect(() => {
    if (!transformControlsRef.current || !sceneRef.current) return;
    
    const transformControls = transformControlsRef.current;
    
    // Update bone helper colors based on selection
    boneHelpersRef.current.forEach(helper => {
      if (helper.material) {
        const isSelected = helper.userData.boneName === selectedBone;
        helper.material.color.setHex(isSelected ? 0xff00ff : 0x00ffff);
        helper.material.opacity = isSelected ? 1.0 : 0.7;
        // Make selected bone helper larger
        const scale = isSelected ? 2.0 : 1.0;
        helper.scale.setScalar(scale);
      }
    });
    
    // Only attach transform controls when posing is enabled
    if (!enablePosing) {
      transformControls.detach();
      transformControls.visible = false;
      return;
    }
    
    if (selectedBone && bonesRef.current[selectedBone]) {
      const bone = bonesRef.current[selectedBone];
      
      // Detach first
      transformControls.detach();
      
      // Calculate appropriate gizmo size based on model scale
      // Use a larger fixed size for better visibility, with minimum of 0.5
      let gizmoSize = 0.8;
      if (modelRef.current) {
        const box = new THREE.Box3().setFromObject(modelRef.current);
        const modelSize = box.getSize(new THREE.Vector3());
        const maxDimension = Math.max(modelSize.x, modelSize.y, modelSize.z);
        gizmoSize = Math.max(0.5, maxDimension * 0.4); // 40% of model size, min 0.5
      }
      
      transformControls.setSize(gizmoSize);
      transformControls.attach(bone);
      transformControls.visible = true;
      transformControls.enabled = true;
      
      // Make gizmo render on top of everything by setting higher render order
      // Check if traverse is available (it should be on Object3D)
      if (transformControls.traverse && typeof transformControls.traverse === 'function') {
        transformControls.traverse((child) => {
          if (child.material) {
            child.material.depthTest = false;
            child.renderOrder = 1000;
          }
        });
      } else {
        // Fallback: directly set properties on the gizmo
        transformControls.renderOrder = 1000;
      }
    } else {
      transformControls.detach();
      transformControls.visible = false;
    }
  }, [selectedBone, enablePosing]);

  // Toggle bone helper visibility based on posing mode
  useEffect(() => {
    boneHelpersRef.current.forEach(helper => {
      helper.visible = enablePosing;
    });
  }, [enablePosing]);

  // Update background color
  useEffect(() => {
    if (!sceneRef.current) return;
    sceneRef.current.background = backgroundColor === 'transparent' 
      ? null 
      : new THREE.Color(backgroundColor);
  }, [backgroundColor]);

  if (!modelUrl) {
    return (
      <div className={`w-full h-full flex items-center justify-center bg-slate-800 ${className}`}>
        <div className="text-center text-slate-400">
          <svg className="w-16 h-16 mx-auto mb-3 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
          <p>No model loaded</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`w-full h-full relative ${className}`}>
      {loading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-slate-800/90 pointer-events-none">
          <div className="flex flex-col items-center gap-3">
            <div className="w-10 h-10 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm text-white">Loading 3D model...</span>
          </div>
        </div>
      )}
      
      {error && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-slate-800">
          <div className="text-center text-red-400 p-4">
            <p className="font-medium">Error</p>
            <p className="text-sm opacity-70">{error}</p>
          </div>
        </div>
      )}
      
      {/* Camera controls hint - show when onCameraChange is available */}
      {onCameraChange && !loading && !error && (
        <div className="absolute bottom-2 left-2 z-10 pointer-events-none">
          <div className="bg-black/60 backdrop-blur-sm rounded px-2 py-1 text-[10px] text-slate-300 space-y-0.5">
            <div className="flex items-center gap-1">
              <span className="text-slate-500">Scroll:</span>
              <span>Zoom</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-slate-500">Shift+Drag:</span>
              <span>Pan</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-slate-500">Alt+Drag:</span>
              <span>Orbit</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-slate-500">Ctrl+Alt+Drag:</span>
              <span>Roll</span>
            </div>
          </div>
        </div>
      )}
      
      <div ref={containerRef} className="w-full h-full" />
    </div>
  );
});

export default ThreeCanvas;
